import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../components/ui/button";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from "../components/ui/card";
import { 
  ArrowLeft, 
  AlertTriangle, 
  AlertCircle, 
  Search, 
  Filter,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock
} from "lucide-react"; 
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { fetchWithAuth } from '../utils/api';
import { apiConfig } from "src/config/apiConfig";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { cn } from "src/utils/cn";

// Error Boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Component error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg">
          <h3 className="text-lg font-bold text-red-500 mb-2">Something went wrong</h3>
          <p className="text-red-500 mb-2">{this.state.error?.message || "Unknown error"}</p>
          <Button 
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
          >
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Update the Round interface to handle both column naming patterns
interface Round {
  id: number;
  format: string;
  format_display: string;
  motion: {
    text: string;
    theme: {
      name: string;
    }
  };
  status: string;
  created_at: string;
  started_at: string | null;
  completed_at?: string | null;
  completed?: string | null;
  created_by: {
    id: number;
    username: string;
  };
  moderator?: {
    id: number;
    username: string;
  };
  round_code: string;
  participant_count: number;
  completed_time?: string | null;
}

// Status options for filter
const STATUS_OPTIONS = [
  { value: "ALL", label: "All Statuses" },
  { value: "SETUP", label: "Setup" },
  { value: "ALLOCATION", label: "Allocation" },
  { value: "ACTIVE", label: "Active" },
  { value: "COMPLETED", label: "Completed" },
  { value: "TERMINATED", label: "Terminated" }
];

// Format options for filter
const FORMAT_OPTIONS = [
  { value: "ALL", label: "All Formats" },
  { value: "BP", label: "British Parliamentary" },
  { value: "AP", label: "Asian Parliamentary" }
];

const ITEMS_PER_PAGE = 10;

const RoundManagement: React.FC = () => {
  const navigate = useNavigate();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rounds, setRounds] = useState<Round[]>([]);
  const [selectedRound, setSelectedRound] = useState<Round | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Add state for terminate AlertDialog
  const [showTerminateAlert, setShowTerminateAlert] = useState(false);
  const [roundToTerminate, setRoundToTerminate] = useState<Round | null>(null);
  const [terminatingRound, setTerminatingRound] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [formatFilter, setFormatFilter] = useState("ALL");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  // Filter and search rounds
  const filteredRounds = useMemo(() => {
    return rounds.filter(round => {
      // Apply status filter
      if (statusFilter !== "ALL" && round.status !== statusFilter) {
        return false;
      }
      
      // Apply format filter
      if (formatFilter !== "ALL") {
        const normalizedFormat = 
          round.format === "ABP" ? "BP" : 
          (round.format === "PDA" || round.format === "AP3") ? "AP" : 
          round.format;
          
        if (normalizedFormat !== formatFilter) {
          return false;
        }
      }
      
      // Apply search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          round.id.toString().includes(query) ||
          (round.round_code && round.round_code.toLowerCase().includes(query)) ||
          (round.moderator?.username && round.moderator.username.toLowerCase().includes(query)) ||
          (round.motion && typeof round.motion === 'object' && round.motion.text && 
            round.motion.text.toLowerCase().includes(query))
        );
      }
      
      return true;
    });
  }, [rounds, statusFilter, formatFilter, searchQuery]);
  
  // Compute pagination
  const totalItems = filteredRounds.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
  const paginatedRounds = filteredRounds.slice(startIndex, endIndex);
  
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, formatFilter, searchQuery]);

  // Memoize fetchRounds to prevent unnecessary rerenders
  const fetchRounds = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Fetching rounds...");
      
      // First, try the regular endpoint with proper error handling
      try {
        const response = await fetch(`${apiConfig.baseURL}/api/admin/rounds/`, {
          headers: {
            'Authorization': `Token ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          cache: 'no-cache'
        });
        
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON');
        }
        
        const data = await response.json();
        console.log("Rounds data:", data);
        
        // Handle both array and object response formats
        let processedRounds: Round[] = [];
        
        if (Array.isArray(data)) {
          processedRounds = normalizeRounds(data);
        } else if (data && data.rounds && Array.isArray(data.rounds)) {
          processedRounds = normalizeRounds(data.rounds);
        } else {
          console.error("Unexpected response format:", data);
          throw new Error("Server returned an invalid response format");
        }
        
        setRounds(processedRounds);
        setError("");
        return;
      } catch (err) {
        console.error("Error with primary endpoint:", err);
        
        // Try fallback endpoint
        try {
          const response = await fetch(`${apiConfig.baseURL}/api/rounds/`, {
            headers: {
              'Authorization': `Token ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            cache: 'no-cache'
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch from fallback: ${response.status}`);
          }
          
          const data = await response.json();
          console.log("Rounds data from fallback:", data);
          
          // Process fallback data with the same format handling
          let processedRounds: Round[] = [];
          if (Array.isArray(data)) {
            processedRounds = normalizeRounds(data);
          } else if (data && data.rounds && Array.isArray(data.rounds)) {
            processedRounds = normalizeRounds(data.rounds);
          } else {
            console.error("Unexpected fallback response format:", data);
            throw new Error("Fallback returned an invalid response format");
          }
          
          setRounds(processedRounds);
          setError("");
          return;
        } catch (fallbackErr) {
          console.error("Error with fallback endpoint:", fallbackErr);
          throw err; // Throw original error
        }
      }
    } catch (err: any) {
      console.error("Fatal error fetching rounds:", err);
      setError("Could not load rounds. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed for this function

  useEffect(() => {
    // Check if token exists before fetching
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required. Please log in again.");
      setLoading(false);
      return;
    }
    
    fetchRounds();
  }, [fetchRounds]);

  // Update normalizeRounds function to better handle moderator info
  const normalizeRounds = (roundsData: any[]): Round[] => {
    return roundsData.map(round => {
      // Ensure round_code is always set to prevent 'N/A'
      if (!round.round_code && round.id) {
        // Generate a temporary code if missing
        const tempCode = `R${String(round.id).padStart(5, '0')}`;
        
        // Log the missing code for debugging
        console.warn(`Round ${round.id} missing round_code, using temporary: ${tempCode}`);
        
        // Update the backend if possible (fire and forget)
        try {
          fetch(`${apiConfig.baseURL}/api/rounds/${round.id}/`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Token ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ round_code: tempCode })
          }).then(res => {
            if (!res.ok) console.error('Failed to update round code');
          }).catch(err => console.error('Error updating round code:', err));
        } catch (e) {
          console.error('Error attempting to fix missing round code:', e);
        }
        
        round.round_code = tempCode;
      }
      
      // Improved created_by handling - always try to get the actual username
      let createdByInfo = null;
      if (round.created_by && typeof round.created_by === 'object' && round.created_by.username) {
        createdByInfo = round.created_by;
      } else if (round.created_by_id || round.creator_id) {
        createdByInfo = {
          id: round.created_by_id || round.creator_id,
          username: round.created_by_name || round.creator_name || 'Creator'  
        };
      }
      
      // Improved moderator handling - always try to get the actual username
      let moderatorInfo = null;
      if (round.moderator && typeof round.moderator === 'object' && round.moderator.username) {
        moderatorInfo = round.moderator;
      } else if (round.moderator_id) {
        moderatorInfo = {
          id: round.moderator_id,
          username: round.moderator_name || 'Moderator'
        };
      }
      
      // If we have no moderator info but have creator info, use that as fallback
      if (!moderatorInfo && createdByInfo) {
        moderatorInfo = {
          id: createdByInfo.id,
          username: createdByInfo.username
        };
      }
      
      // If we have no creator info but have moderator info, use that as fallback
      if (!createdByInfo && moderatorInfo) {
        createdByInfo = {
          id: moderatorInfo.id,
          username: moderatorInfo.username
        };
      }
      
      return {
        id: round.id,
        status: round.status?.toUpperCase() || 'SETUP',
        round_code: round.round_code || round.code || `R${String(round.id).padStart(5, '0')}`,
        // Standardize format codes
        format: round.format === 'ABP' ? 'BP' : 
                (round.format === 'PDA' || round.format === 'AP3') ? 'AP' : 
                round.format,
        format_display: round.format === 'ABP' || round.format === 'BP' ? 'British Parliamentary' : 
                       (round.format === 'PDA' || round.format === 'AP3' || round.format === 'AP') ? 'Asian Parliamentary' : 
                       round.format,
        created_at: round.created_at,
        started_at: round.started_at || round.started || null,
        completed: round.completed || round.completed_at || null,
        participants_count: round.participants_count || 0,
        participant_count: round.participant_count || round.participants_count || 0,
        motion: round.motion?.text ? {
          text: round.motion.text,
          theme: {
            name: round.motion?.theme?.name || ''
          }
        } : {
          text: round.motion_text || 'No motion',
          theme: { name: '' }
        },
        created_by: createdByInfo || {
          id: 0,
          username: 'Unassigned'
        },
        moderator: moderatorInfo || {
          id: 0,
          username: 'Unassigned'
        },
        completed_time: round.completed_time || null,
      };
    });
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  const handleDeleteRound = async () => {
    if (!selectedRound) return;
    
    try {
      setLoading(true);
      setIsDeleting(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("Authentication required");
      }
      
      console.log(`Attempting to delete round ${selectedRound.id}`);
      
      // Try multiple endpoints in sequence until one works
      let deleted = false;
      
      // Array of endpoints to try in order
      const endpoints = [
        `/api/rounds/${selectedRound.id}/simple-delete/`,
        `/rounds/${selectedRound.id}/simple-delete/`,
        `/api/rounds/${selectedRound.id}/delete/`,
        `/rounds/${selectedRound.id}/delete/`,
        `/admin/rounds/${selectedRound.id}/delete/`,
        `/api/admin/rounds/${selectedRound.id}/`
      ];
      
      // Try each endpoint until one works
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying to delete round via endpoint: ${endpoint}`);
          const response = await fetch(`${apiConfig.baseURL}${endpoint}`, {
            method: endpoint.includes('delete') ? 'POST' : 'DELETE',
            headers: {
              'Authorization': `Token ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            deleted = true;
            console.log(`Round deleted successfully via ${endpoint}`);
            break;
          } else {
            const text = await response.text();
            console.log(`Endpoint ${endpoint} failed:`, text);
          }
        } catch (err) {
          console.error(`Error with endpoint ${endpoint}:`, err);
        }
      }
      
      if (deleted) {
        // Update UI on success
        setRounds(rounds.filter(r => r.id !== selectedRound.id));
        setSelectedRound(null);
        setShowDeleteDialog(false);
        toast.success(`Round #${selectedRound.id} deleted successfully`);
      } else {
        toast.error("Failed to delete round. Please try again later.");
        // Force refresh rounds list to see if it was actually deleted
        fetchRounds();
      }
      
    } catch (err: any) {
      console.error("Error in handleDeleteRound:", err);
      toast.error(err.message || "Failed to delete round");
      // Force refresh
      fetchRounds();
    } finally {
      setLoading(false);
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleTerminateRound = async (roundId: number) => {
    // Find the round to terminate
    const round = rounds.find(r => r.id === roundId);
    if (!round) {
      toast.error("Round not found");
      return;
    }
    
    setRoundToTerminate(round);
    setShowTerminateAlert(true);
  };

  const confirmTerminateRound = async () => {
    if (!roundToTerminate) return;
    
    try {
      setTerminatingRound(true);
      
      // Try multiple endpoints in sequence
      const endpoints = [
        `/api/round/${roundToTerminate.id}/terminate/`,
        `/api/rounds/${roundToTerminate.id}/terminate/`,
        `/round/${roundToTerminate.id}/terminate/`,
        `/admin/rounds/${roundToTerminate.id}/terminate/`
      ];
      
      let success = false;
      let lastError = null;
      
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying to terminate round via endpoint: ${endpoint}`);
          await fetchWithAuth(endpoint, {
            method: "POST"
          });
          
          success = true;
          console.log(`Successfully terminated round via ${endpoint}`);
          break;
        } catch (err) {
          console.error(`Failed with endpoint ${endpoint}:`, err);
          lastError = err;
        }
      }
      
      if (!success) {
        throw lastError || new Error("Failed to terminate round through all endpoints");
      }
      
      // Refresh rounds to get updated state
      fetchRounds();
      
      // Show success message
      toast.success("Round terminated successfully");
    } catch (err: any) {
      toast.error(err.message || "Error terminating round");
      console.error("Error terminating round:", err);
    } finally {
      setTerminatingRound(false);
      setShowTerminateAlert(false);
      setRoundToTerminate(null);
    }
  };

  // Format date in a more readable format
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SETUP':
        return 'bg-gray-500';
      case 'ALLOCATION':
        return 'bg-blue-500';
      case 'ACTIVE':
        return 'bg-green-500';
      case 'COMPLETED':
        return 'bg-purple-500';
      case 'TERMINATED':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  
  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <ErrorBoundary>
      <motion.div 
        className="p-4 md:p-6"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={containerVariants}
      >
        <Card className="w-full mx-auto transition-colors duration-200 bg-card shadow-lg rounded-lg">
          <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-6 border-b gap-4">
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBack}
                className="mr-2 hidden sm:flex"
              >
                <ArrowLeft size={16} />
              </Button>
              <div>
                <CardTitle className="text-xl sm:text-2xl font-bold">Round Management</CardTitle>
                <CardDescription className="mt-1">Create, view, and manage debate rounds</CardDescription>
              </div>
            </div>
            <Button onClick={() => navigate('/round/setup')} className="bg-primary hover:bg-primary/90 w-full sm:w-auto">
              Create New Round
            </Button>
          </CardHeader>

          <CardContent className="p-4 md:p-6">
            {error && (
              <motion.div 
                variants={itemVariants}
                className="mb-6 p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg text-red-500"
              >
                <div className="flex items-center">
                  <AlertTriangle className="mr-2" size={20} />
                  {error}
                </div>
              </motion.div>
            )}
            
            {/* Search and filters - make more responsive */}
            <motion.div 
              variants={itemVariants}
              className="mb-6 grid grid-cols-1 gap-4"
            >
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by ID, code, moderator, or motion..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select
                  value={formatFilter}
                  onValueChange={setFormatFilter}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by format" />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMAT_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </motion.div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading rounds...</p>
              </div>
            ) : filteredRounds.length > 0 ? (
              <motion.div variants={itemVariants}>
                {/* Mobile view - card based layout for small screens */}
                <div className="block md:hidden space-y-4 mb-4">
                  {paginatedRounds.map((round) => (
                    <div key={round.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">Round #{round.id}</p>
                          <p className="text-sm text-muted-foreground">{round.format_display}</p>
                        </div>
                        <Badge className={`${getStatusColor(round.status)} text-white`}>
                          {round.status}
                        </Badge>
                      </div>
                      
                      <div className="mt-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>{formatDate(round.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-medium">Moderator:</span>
                          <span>{round.moderator?.username || round.created_by?.username || 'Unassigned'}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-medium">Code:</span>
                          <span className="font-mono">{round.round_code}</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button 
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(`/round/${round.id}`)}
                          className="flex-1"
                        >
                          View
                        </Button>
                        {round.status === 'ACTIVE' && (
                          <Button 
                            size="sm"
                            variant="destructive"
                            onClick={() => handleTerminateRound(round.id)}
                            className="flex-1"
                          >
                            Terminate
                          </Button>
                        )}
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedRound(round);
                            setShowDeleteDialog(true);
                          }}
                          className="flex-1"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Desktop view - table layout */}
                <div className="hidden md:block overflow-x-auto rounded-md border mb-4">
                  <table className="min-w-full divide-y">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Format</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Moderator</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {paginatedRounds.map((round) => (
                        <tr key={round.id} className="hover:bg-muted/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap font-medium">{round.id}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{round.format_display}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={`${getStatusColor(round.status)} text-white`}>
                              {round.status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                              {formatDate(round.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              {round.moderator?.username || round.created_by?.username || 'Unassigned'}
                              {(round.moderator?.id === round.created_by?.id && round.moderator?.username) || 
                               (!round.moderator?.username && round.created_by?.username) ? 
                                <Badge variant="secondary" className="ml-1 text-xs">Creator</Badge> : null}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-mono">
                            {round.round_code}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <Button 
                                size="sm"
                                variant="secondary"
                                onClick={() => navigate(`/round/${round.id}`)}
                              >
                                View
                              </Button>
                              {round.status === 'ACTIVE' && (
                                <Button 
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleTerminateRound(round.id)}
                                >
                                  Terminate
                                </Button>
                              )}
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedRound(round);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination controls - make more responsive */}
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-muted-foreground order-2 sm:order-1">
                    Showing {startIndex + 1}-{endIndex} of {totalItems} rounds
                  </div>
                  <div className="flex items-center space-x-2 order-1 sm:order-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <div className="text-sm px-2">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-4">No rounds found</p>
                {(statusFilter !== "ALL" || formatFilter !== "ALL" || searchQuery) && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setStatusFilter("ALL");
                      setFormatFilter("ALL");
                      setSearchQuery("");
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            )}
            
            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogContent className="max-w-md mx-auto">
                <DialogHeader>
                  <DialogTitle>Delete Round</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this round? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                
                {selectedRound && (
                  <div className="py-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="font-medium">Round #{selectedRound.id}</p>
                      <p className="text-sm text-muted-foreground mt-1">{selectedRound.format_display}</p>
                      <p className="text-sm mt-2">
                        Motion: {selectedRound.motion?.text || 'No motion available'}
                      </p>
                      <div className="flex flex-wrap items-center justify-between mt-2 gap-2">
                        <Badge variant="outline" className="mt-2">Code: {selectedRound.round_code}</Badge>
                        <Badge variant="outline" className={cn("mt-2", getStatusColor(selectedRound.status), "text-white")}>{selectedRound.status}</Badge>
                      </div>
                      {selectedRound.status === 'ACTIVE' && (
                        <p className="mt-2 text-amber-500 text-sm flex items-center">
                          <AlertTriangle size={14} className="mr-1" />
                          This round is currently active with {selectedRound.participant_count || 0} participants
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleDeleteRound}
                    disabled={isDeleting}
                    className="w-full sm:w-auto"
                  >
                    {isDeleting ? (
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent"></div>
                        Deleting...
                      </>
                    ) : (
                      'Delete Round'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Terminate Round Confirmation Dialog */}
            <AlertDialog open={showTerminateAlert} onOpenChange={setShowTerminateAlert}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Terminate Round</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to terminate Round #{roundToTerminate?.id}? 
                    This will end the round immediately and cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={terminatingRound} onClick={() => { setShowTerminateAlert(false); setRoundToTerminate(null); }}>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={confirmTerminateRound}
                    disabled={terminatingRound}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {terminatingRound ? "Terminating..." : "Terminate Round"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </motion.div>
    </ErrorBoundary>
  );
};

export default RoundManagement;
